import React from "react";

import { Icon, type IconName } from "./icons";

type Variant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "danger-ghost"
  | "link";

type Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> & {
  variant?: Variant;
  size?: "sm" | "md";
  icon?: IconName;
  iconRight?: IconName;
  type?: "button" | "submit";
  busy?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, Props>(
  function Button(
    {
      variant = "secondary",
      size = "md",
      icon,
      iconRight,
      type = "button",
      busy,
      className,
      children,
      disabled,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={`ds-btn ${variant} ${size}${className ? ` ${className}` : ""}`}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        {...rest}
      >
        {icon && <Icon name={icon} size={size === "sm" ? 14 : 16} />}
        {children}
        {iconRight && <Icon name={iconRight} size={size === "sm" ? 14 : 16} />}
      </button>
    );
  },
);

/** A link styled as a button, for navigation that leaves helm. */
export function LinkButton({
  variant = "secondary",
  size = "md",
  icon,
  iconRight,
  className,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant;
  size?: "sm" | "md";
  icon?: IconName;
  iconRight?: IconName;
}) {
  return (
    <a
      className={`ds-btn ${variant} ${size}${className ? ` ${className}` : ""}`}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === "sm" ? 14 : 16} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "sm" ? 14 : 16} />}
    </a>
  );
}

export const IconButton = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "children"> & {
    icon: IconName;
    label: string;
    variant?: "ghost" | "secondary" | "danger";
    size?: "sm" | "md";
  }
>(function IconButton(
  { icon, label, variant = "ghost", size = "md", className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={`ds-iconbtn ${variant} ${size}${className ? ` ${className}` : ""}`}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} size={size === "sm" ? 14 : 16} />
    </button>
  );
});

/** The one table export control: a small ghost "CSV" button in the card
 * header, announced as a download. Busy until ``onClick`` settles; a rejection
 * prints beside the button. */
export function CsvButton({
  onClick,
}: {
  onClick: () => void | Promise<unknown>;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onClick();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't download the CSV.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error && (
        <span className="ds-csv-error" role="alert">
          {error}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon="download"
        busy={busy}
        aria-label="Download CSV"
        onClick={() => void run()}
      >
        CSV
      </Button>
    </>
  );
}
